import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { adminAPI } from "../lib/api";
import { formatCurrency } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Copy, Trash2, Eye, Users, Download, Calendar, RefreshCw, ChevronDown } from "lucide-react";

const COMMISSION_TYPE_LABELS = {
  one_time: "One-Time",
  perpetual: "Perpetual",
};

const COMMISSION_TYPE_HELPER = {
  one_time: "Earn once when the referred user becomes a paid subscriber",
  perpetual: "Earn every time the referred user successfully renews while subscribed",
};

function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return dateStr;
  }
}

function CommissionTypeBadge({ type }) {
  const label = COMMISSION_TYPE_LABELS[type] || type;
  if (type === "perpetual") {
    return (
      <Badge variant="outline" className="bg-purple-500/10 text-purple-600 border-purple-500/20">
        <RefreshCw className="h-3 w-3 mr-1" />
        {label}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20">
      {label}
    </Badge>
  );
}

export default function AffiliatesPage() {
  const navigate = useNavigate();
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    commission_percent: 10,
    commission_type: "one_time",
    gcash_number: "",
    paypal_email: "",
    notes: "",
  });
  const [filterType, setFilterType] = useState("all");

  const [referralCounts, setReferralCounts] = useState({});
  const [referrals, setReferrals] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  const [dateRange, setDateRange] = useState({
    start_date: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
  });
  const [payoutForm, setPayoutForm] = useState({ paid_amount: "", payment_method: "gcash", payment_date: new Date().toISOString().split('T')[0] });
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);

  useEffect(() => {
    checkAdminAccess();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAdminAccess = async () => {
    try {
      const res = await adminAPI.checkAccess();
      setIsAdmin(res.data.is_admin);
      if (res.data.is_admin) {
        fetchAffiliates();
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error("Failed to check admin access:", err);
      setLoading(false);
    }
  };

  const fetchAffiliates = async () => {
    try {
      const res = await adminAPI.listAffiliates();
      setAffiliates(res.data);
      // load referral counts for table
      loadReferralCounts(res.data);
    } catch (err) {
      console.error("Failed to fetch affiliates:", err);
      toast.error("Failed to load affiliates");
    } finally {
      setLoading(false);
    }
  };

  const loadReferralCounts = async (affs) => {
    const counts = {};
    try {
      await Promise.all(affs.map(async (a) => {
        try {
          const res = await adminAPI.getReferrals(a.id);
          const activeCount = (res.data || []).filter(r => r.subscription_active).length;
          counts[a.id] = activeCount;
        } catch (e) {
          counts[a.id] = 0;
        }
      }));
    } catch (e) {
      console.error('Failed loading referral counts', e);
    }
    setReferralCounts(counts);
  };

  const handleViewDetails = (affiliate) => {
    setSelectedAffiliate(affiliate);
    setShowDetailsDialog(true);
    loadReferralData(affiliate);
  };

  const loadReferralData = async (affiliate) => {
    setLoadingReferrals(true);
    try {
      const [refRes, statsRes] = await Promise.all([
        adminAPI.getReferrals(affiliate.id),
        adminAPI.getReferralStats(affiliate.id, dateRange.start_date, dateRange.end_date),
      ]);
      setReferrals(refRes.data || []);
      setReferralStats(statsRes.data || null);
    } catch (err) {
      console.error("Failed to load referral data:", err);
      toast.error("Failed to load referral data");
    } finally {
      setLoadingReferrals(false);
    }
  };

  const handleFetchStats = async () => {
    if (!selectedAffiliate) return;
    try {
      const statsRes = await adminAPI.getReferralStats(selectedAffiliate.id, dateRange.start_date, dateRange.end_date);
      setReferralStats(statsRes.data);
    } catch (err) {
      console.error('Failed to fetch stats', err);
      toast.error('Failed to fetch stats');
    }
  };

  const handleOpenPayout = (commission) => {
    setPayoutForm({ paid_amount: commission ? commission.amount : (referralStats?.total_earned || 0), payment_method: selectedAffiliate.gcash_number ? 'gcash' : 'paypal', payment_date: new Date().toISOString().split('T')[0] });
    setShowPayoutDialog(true);
  };

  const handleGeneratePayout = async () => {
    if (!selectedAffiliate) return;
    try {
      const payload = {
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
        paid_amount: parseFloat(payoutForm.paid_amount || 0),
        payment_method: payoutForm.payment_method,
        payment_date: payoutForm.payment_date,
      };
      const res = await adminAPI.generatePaymentReceipt(selectedAffiliate.id, payload);
      const w = window.open();
      w.document.write(res.data.html);
      w.document.close();
      setTimeout(() => w.print(), 300);
      toast.success('Payment receipt generated. Use print/save as PDF.');
      setShowPayoutDialog(false);
    } catch (err) {
      console.error('Failed to generate payout', err);
      toast.error('Failed to generate payout');
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      await adminAPI.createAffiliate(payload);
      toast.success("Affiliate created");
      setShowAddDialog(false);
      resetForm();
      fetchAffiliates();
    } catch (err) {
      console.error("Failed to create affiliate:", err);
      toast.error(err.response?.data?.detail || "Failed to create affiliate");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this affiliate?")) return;
    try {
      await adminAPI.deleteAffiliate(id);
      toast.success("Affiliate deleted");
      fetchAffiliates();
    } catch (err) {
      console.error("Failed to delete affiliate:", err);
      toast.error("Failed to delete affiliate");
    }
  };

  const handleMarkPaid = async (affiliate) => {
    const pendingCommissions = affiliate.commissions?.filter((c) => c.status === "pending") || [];
    if (pendingCommissions.length === 0) {
      toast.info("No pending commissions");
      return;
    }

    try {
      const commission = pendingCommissions[0];
      await adminAPI.markCommissionPaid(affiliate.id, commission.id);
      toast.success("Commission marked as paid");
      fetchAffiliates();
    } catch (err) {
      console.error("Failed to mark commission as paid:", err);
      toast.error("Failed to mark commission as paid");
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Affiliate code copied!");
  };

  const handleCopyLink = (link) => {
    let final = link;
    if (final.startsWith("/")) {
      final = window.location.origin + final;
    }
    navigator.clipboard.writeText(final);
    toast.success("Affiliate link copied!");
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      commission_percent: 10,
      commission_type: "one_time",
      gcash_number: "",
      paypal_email: "",
      notes: "",
    });
  };

  const handleExportAffiliates = async () => {
    try {
      const response = await adminAPI.exportAffiliates();
      const today = new Date().toISOString().split("T")[0];
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `affiliates_export_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success("Affiliates exported successfully");
    } catch (err) {
      console.error("Export error:", err);
      let errorMsg = err.message;
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          errorMsg = json.detail || json.message || text;
        } catch {
          errorMsg = err.message;
        }
      } else {
        errorMsg = err.response?.data?.detail || err.message;
      }
      toast.error(`Export failed: ${errorMsg}`);
    }
  };

  const handleExportCommissionLedger = async () => {
    try {
      const today = new Date().toISOString().split("T")[0];
      const response = await adminAPI.exportCommissionLedger();
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `commission_ledger_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success("Commission ledger exported successfully");
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Failed to export commission ledger");
    }
  };

  const handleExportAffiliateFullReport = async (affiliate) => {
    try {
      const today = new Date().toISOString().split("T")[0];
      const safeName = (affiliate.name || affiliate.id).replace(/\s+/g, "_");
      const response = await adminAPI.exportAffiliateFullReport(affiliate.id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `affiliate_${safeName}_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success("Affiliate report exported successfully");
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Failed to export affiliate report");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium mb-2">Access Denied</h3>
          <p className="text-muted-foreground">You need admin access to view this page</p>
        </div>
      </div>
    );
  }

  const totalEarned = affiliates.reduce((sum, a) => sum + (a.total_earned || 0), 0);
  const totalPending = affiliates.reduce((sum, a) => sum + (a.total_pending || 0), 0);
  const totalPaid = affiliates.reduce((sum, a) => sum + (a.total_paid || 0), 0);

  const filteredAffiliates = affiliates.filter((a) => {
    if (filterType === "one_time") return (a.commission_type || "one_time") === "one_time";
    if (filterType === "perpetual") return a.commission_type === "perpetual";
    if (filterType === "active") return a.is_active !== false;
    if (filterType === "pending") return (a.total_pending || 0) > 0;
    return true;
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="affiliates-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Affiliates</h1>
          <p className="text-muted-foreground mt-1">
            Manage affiliate partners and commission tracking
          </p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="export-affiliates-btn">
                <Download className="h-4 w-4" />
                Export CSV
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportAffiliates}>
                <Download className="h-4 w-4 mr-2" />
                Export Affiliates CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportCommissionLedger}>
                <Download className="h-4 w-4 mr-2" />
                Export Commission Ledger CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            onClick={() => {
              resetForm();
              setShowAddDialog(true);
            }}
            className="gap-2"
            data-testid="add-affiliate-btn"
          >
            <Plus className="h-4 w-4" />
            Add Affiliate
          </Button>
        </div>
      </div>

      {/* Commission Stats */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Earned</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatCurrency(totalEarned, "PHP")}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pending Payout</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-yellow-600">{formatCurrency(totalPending, "PHP")}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Paid Out</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(totalPaid, "PHP")}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap gap-2">
        {[
          { value: "all", label: "All Affiliates" },
          { value: "one_time", label: "One-Time" },
          { value: "perpetual", label: "Perpetual" },
          { value: "active", label: "Active Only" },
          { value: "pending", label: "Pending Payout" },
        ].map((f) => (
          <Button
            key={f.value}
            variant={filterType === f.value ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterType(f.value)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {/* Affiliates Table */}
      {affiliates.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">No affiliates yet</h3>
            <p className="text-muted-foreground mb-4">
              Start by adding your first affiliate partner
            </p>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Affiliate
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date Joined</TableHead>
                <TableHead>Commission %</TableHead>
                <TableHead>Commission Type</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Total Earned</TableHead>
                <TableHead>Pending</TableHead>
                <TableHead>Active Referrals</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAffiliates.map((affiliate) => (
                <TableRow key={affiliate.id} className={affiliate.is_active === false ? "opacity-60" : ""}>
                  <TableCell className="font-medium">
                    {affiliate.name}
                  </TableCell>
                  <TableCell className="text-sm">{affiliate.email}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        affiliate.is_active === false
                          ? "bg-red-500/10 text-red-600 border-red-500/20"
                          : "bg-green-500/10 text-green-600 border-green-500/20"
                      }
                    >
                      {affiliate.is_active === false ? "Inactive" : "Active"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(affiliate.created_at)}
                  </TableCell>
                  <TableCell>{affiliate.commission_percent}%</TableCell>
                  <TableCell>
                    <CommissionTypeBadge type={affiliate.commission_type || "one_time"} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-muted px-2 py-1 rounded">
                        {affiliate.affiliate_code}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleCopyCode(affiliate.affiliate_code)}
                        className="h-6 w-6"
                        data-testid={`copy-code-${affiliate.id}`}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="font-semibold">
                    {formatCurrency(affiliate.total_earned || 0, "PHP")}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        affiliate.total_pending > 0
                          ? "bg-yellow-500/10 text-yellow-600 border-yellow-500/20"
                          : "bg-green-500/10 text-green-600 border-green-500/20"
                      }
                    >
                      {formatCurrency(affiliate.total_pending || 0, "PHP")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{referralCounts[affiliate.id] ?? 0}</div>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleViewDetails(affiliate)}>
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        {affiliate.total_pending > 0 && (
                          <DropdownMenuItem onClick={() => handleMarkPaid(affiliate)}>
                            <span className="text-green-600">Mark as Paid</span>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => handleDelete(affiliate.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Payout Dialog */}
      <Dialog open={showPayoutDialog} onOpenChange={setShowPayoutDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Generate Payout Receipt</DialogTitle>
            <DialogDescription>Fill payment details and generate PDF receipt</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Amount</Label>
              <Input type="number" value={payoutForm.paid_amount} onChange={(e) => setPayoutForm({...payoutForm, paid_amount: e.target.value})} />
            </div>
            <div>
              <Label>Payment Method</Label>
              <select value={payoutForm.payment_method} onChange={(e) => setPayoutForm({...payoutForm, payment_method: e.target.value})} className="w-full border px-2 py-1 rounded">
                <option value="gcash">GCash</option>
                <option value="paypal">PayPal</option>
              </select>
            </div>
            <div>
              <Label>Payment Date</Label>
              <Input type="date" value={payoutForm.payment_date} onChange={(e) => setPayoutForm({...payoutForm, payment_date: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayoutDialog(false)}>Cancel</Button>
            <Button onClick={handleGeneratePayout}>Generate & Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Affiliate Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Affiliate</DialogTitle>
            <DialogDescription>
              Create a new affiliate partner account
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="affiliate-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="affiliate-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="commission_type">Commission Type *</Label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: "one_time", label: "One-Time Commission" },
                  { value: "perpetual", label: "Perpetual Commission" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, commission_type: opt.value })}
                    className={`p-3 rounded-lg border-2 text-left transition-colors ${
                      formData.commission_type === opt.value
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-muted-foreground/40"
                    }`}
                    data-testid={`commission-type-${opt.value}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <CommissionTypeBadge type={opt.value} />
                    </div>
                    <p className="text-xs text-muted-foreground">{COMMISSION_TYPE_HELPER[opt.value]}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="commission">Commission % *</Label>
                <Input
                  id="commission"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.commission_percent}
                  onChange={(e) =>
                    setFormData({ ...formData, commission_percent: parseFloat(e.target.value) })
                  }
                  required
                  data-testid="affiliate-commission-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gcash">GCash Number</Label>
                <Input
                  id="gcash"
                  value={formData.gcash_number}
                  onChange={(e) => setFormData({ ...formData, gcash_number: e.target.value })}
                  placeholder="+63..."
                  data-testid="affiliate-gcash-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="paypal">PayPal Email</Label>
                <Input
                  id="paypal"
                  type="email"
                  value={formData.paypal_email}
                  onChange={(e) => setFormData({ ...formData, paypal_email: e.target.value })}
                  data-testid="affiliate-paypal-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Internal Notes<span className="text-muted-foreground font-normal"> (admin only)</span></Label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="e.g. Trusted partner / sister / lifetime recurring deal"
                rows={2}
                className="w-full border rounded-md px-3 py-2 text-sm bg-background text-foreground border-border resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="affiliate-notes-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="create-affiliate-btn">
                Create Affiliate
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Affiliate Details Dialog */}
      {selectedAffiliate && (
        <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                {selectedAffiliate.name}
                <CommissionTypeBadge type={selectedAffiliate.commission_type || "one_time"} />
                <Badge
                  variant="outline"
                  className={
                    selectedAffiliate.is_active === false
                      ? "bg-red-500/10 text-red-600 border-red-500/20"
                      : "bg-green-500/10 text-green-600 border-green-500/20"
                  }
                >
                  {selectedAffiliate.is_active === false ? "Inactive" : "Active"}
                </Badge>
              </DialogTitle>
              <DialogDescription>{selectedAffiliate.email}</DialogDescription>
            </DialogHeader>
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground font-medium">Date Joined</p>
                  <p className="text-sm font-semibold">{formatDate(selectedAffiliate.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium">Commission Rate</p>
                  <p className="text-lg font-semibold">{selectedAffiliate.commission_percent}%</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium">Commission Type</p>
                  <p className="text-sm font-medium mt-1">
                    {COMMISSION_TYPE_LABELS[selectedAffiliate.commission_type] || "One-Time"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium">Total Earned</p>
                  <p className="text-lg font-semibold">
                    {formatCurrency(selectedAffiliate.total_earned || 0, "PHP")}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium">Total Paid Out</p>
                  <p className="text-lg font-semibold text-green-600">
                    {formatCurrency(selectedAffiliate.total_paid || 0, "PHP")}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium">Pending Payout</p>
                  <p className="text-lg font-semibold text-yellow-600">
                    {formatCurrency(selectedAffiliate.total_pending || 0, "PHP")}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium">Total Referrals</p>
                  <p className="text-lg font-semibold">{(referrals || []).length}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium">Active Paid Referrals</p>
                  <p className="text-lg font-semibold text-green-600">
                    {(referrals || []).filter(r => r.subscription_active).length}
                  </p>
                </div>
              </div>

              {selectedAffiliate.notes && (
                <div className="p-3 bg-muted/50 rounded-md border-l-4 border-primary/30">
                  <p className="text-xs text-muted-foreground font-medium mb-1">Internal Notes</p>
                  <p className="text-sm">{selectedAffiliate.notes}</p>
                </div>
              )}

              {/* Affiliate Code & Link */}
              <div className="border-t pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground font-medium mb-1">Affiliate Code</p>
                  <div className="flex items-center gap-2">
                    <code className="text-sm bg-muted px-3 py-2 rounded flex-1">
                      {selectedAffiliate.affiliate_code}
                    </code>
                    <Button variant="outline" size="sm" onClick={() => handleCopyCode(selectedAffiliate.affiliate_code)}>
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium mb-1">Affiliate Link</p>
                  <div className="flex items-center gap-2">
                    <a
                      href={(selectedAffiliate.affiliate_link || "").startsWith("/")
                        ? window.location.origin + selectedAffiliate.affiliate_link
                        : selectedAffiliate.affiliate_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 break-all"
                    >
                      <code className="text-xs bg-muted px-3 py-2 rounded block">
                        {(selectedAffiliate.affiliate_link || "").startsWith("/")
                          ? window.location.origin + selectedAffiliate.affiliate_link
                          : selectedAffiliate.affiliate_link}
                      </code>
                    </a>
                    <Button variant="outline" size="sm" onClick={() => handleCopyLink(selectedAffiliate.affiliate_link)}>
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                {selectedAffiliate.gcash_number && (
                  <div>
                    <p className="text-xs text-muted-foreground font-medium">GCash Number</p>
                    <p className="text-sm">{selectedAffiliate.gcash_number}</p>
                  </div>
                )}
                {selectedAffiliate.paypal_email && (
                  <div>
                    <p className="text-xs text-muted-foreground font-medium">PayPal Email</p>
                    <p className="text-sm">{selectedAffiliate.paypal_email}</p>
                  </div>
                )}
              </div>

              {/* Date range + stats refresh */}
              <div className="border-t pt-4 space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <input type="date" value={dateRange.start_date} onChange={(e) => setDateRange({...dateRange, start_date: e.target.value})} className="border px-2 py-1 rounded bg-background text-foreground border-border dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600 [color-scheme:light] dark:[color-scheme:dark]" />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">to</span>
                    <input type="date" value={dateRange.end_date} onChange={(e) => setDateRange({...dateRange, end_date: e.target.value})} className="border px-2 py-1 rounded bg-background text-foreground border-border dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600 [color-scheme:light] dark:[color-scheme:dark]" />
                  </div>
                  <Button variant="outline" size="sm" onClick={handleFetchStats}>
                    Refresh Stats
                  </Button>
                  <div className="ml-auto text-sm">
                    <div>Active Referrals: <strong>{referralStats?.total_referrals ?? (referrals || []).filter(r => r.subscription_active).length}</strong></div>
                    <div>Period Earned: <strong>{formatCurrency(referralStats?.total_earned || 0, 'PHP')}</strong></div>
                  </div>
                </div>
              </div>

              {/* Referred Accounts Table */}
              <div className="border-t pt-4">
                <p className="text-sm font-semibold mb-3">Referred Accounts</p>
                {loadingReferrals ? (
                  <div className="text-sm text-muted-foreground">Loading referrals...</div>
                ) : (referrals || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No referred accounts yet.</p>
                ) : (
                  <div className="overflow-x-auto rounded border">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Signup Date</TableHead>
                          <TableHead>Subscription</TableHead>
                          <TableHead>Paid Until</TableHead>
                          <TableHead>Total Commissions</TableHead>
                          <TableHead>Paid Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(referrals || []).map((r) => (
                          <TableRow key={r.user_id}>
                            <TableCell className="font-medium">{r.name || "—"}</TableCell>
                            <TableCell className="text-sm">{r.email || "—"}</TableCell>
                            <TableCell className="text-sm">{formatDate(r.created_at)}</TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={
                                  r.subscription_status === "active"
                                    ? "bg-green-500/10 text-green-600 border-green-500/20"
                                    : "bg-muted text-muted-foreground"
                                }
                              >
                                {r.subscription_status || "unknown"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm">
                              {r.paid_until ? formatDate(r.paid_until) : "—"}
                            </TableCell>
                            <TableCell className="text-sm font-medium">
                              {formatCurrency(r.total_commissions || 0, "PHP")}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={
                                  r.subscription_active
                                    ? "bg-green-500/10 text-green-600 border-green-500/20"
                                    : "bg-red-500/10 text-red-600 border-red-500/20"
                                }
                              >
                                {r.subscription_active ? "Active" : "Inactive"}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              {/* Commission History / Payout Ledger */}
              <div className="border-t pt-4">
                <p className="text-sm font-semibold mb-3">Commission History / Payout Ledger</p>
                {!selectedAffiliate.commissions || selectedAffiliate.commissions.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No commission history yet.</p>
                ) : (
                  <div className="overflow-x-auto rounded border max-h-72 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead>Date Earned</TableHead>
                          <TableHead>Referred User</TableHead>
                          <TableHead>Billing Period</TableHead>
                          <TableHead>Source Amount</TableHead>
                          <TableHead>Commission %</TableHead>
                          <TableHead>Commission Amount</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Paid Date</TableHead>
                          <TableHead>Notes</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedAffiliate.commissions.map((commission) => {
                          const referralUser = (referrals || []).find(r => r.user_id === commission.referred_user_id);
                          return (
                          <TableRow key={commission.id}>
                            <TableCell className="text-sm whitespace-nowrap">
                              {formatDate(commission.created_at)}
                            </TableCell>
                            <TableCell className="text-sm">
                              {referralUser
                                ? `${referralUser.name || ""}${referralUser.email ? ` (${referralUser.email})` : ""}`.trim() || "—"
                                : commission.note || "—"}
                            </TableCell>
                            <TableCell className="text-sm">
                              {commission.billing_period || "—"}
                            </TableCell>
                            <TableCell className="text-sm">
                              {commission.source_amount != null
                                ? formatCurrency(commission.source_amount, "PHP")
                                : "—"}
                            </TableCell>
                            <TableCell className="text-sm">
                              {commission.commission_percent != null && commission.commission_percent !== ""
                                ? `${commission.commission_percent}%`
                                : "—"}
                            </TableCell>
                            <TableCell className="text-sm font-medium">
                              {formatCurrency(commission.amount, "PHP")}
                            </TableCell>
                            <TableCell>
                              {commission.commission_type
                                ? <CommissionTypeBadge type={commission.commission_type} />
                                : "—"}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={
                                  commission.status === "paid"
                                    ? "bg-green-500/10 text-green-600 border-green-500/20"
                                    : "bg-yellow-500/10 text-yellow-600 border-yellow-500/20"
                                }
                              >
                                {commission.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm whitespace-nowrap">
                              {commission.paid_at ? formatDate(commission.paid_at) : "—"}
                            </TableCell>
                            <TableCell className="text-xs max-w-[120px] truncate" title={commission.note}>
                              {commission.note || "—"}
                            </TableCell>
                            <TableCell>
                              {commission.status !== "paid" && (
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={async () => {
                                      try {
                                        const payload = {
                                          start_date: new Date(commission.created_at).toISOString().split('T')[0],
                                          end_date: new Date(commission.created_at).toISOString().split('T')[0],
                                          paid_amount: commission.amount,
                                          payment_method: selectedAffiliate.gcash_number ? 'gcash' : 'paypal',
                                          payment_date: new Date().toISOString().split('T')[0],
                                        };
                                        const res = await adminAPI.generatePaymentReceipt(selectedAffiliate.id, payload);
                                        const w = window.open();
                                        w.document.write(res.data.html);
                                        w.document.close();
                                        setTimeout(() => w.print(), 300);
                                        toast.success('Receipt generated.');
                                      } catch (err) {
                                        console.error('Failed to generate receipt', err);
                                        toast.error('Failed to generate receipt');
                                      }
                                    }}
                                  >
                                    <Download className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={async () => {
                                      if (!window.confirm('Mark this commission as paid and generate receipt?')) return;
                                      try {
                                        const payload = {
                                          start_date: new Date(commission.created_at).toISOString().split('T')[0],
                                          end_date: new Date(commission.created_at).toISOString().split('T')[0],
                                          paid_amount: commission.amount,
                                          payment_method: selectedAffiliate.gcash_number ? 'gcash' : 'paypal',
                                          payment_date: new Date().toISOString().split('T')[0],
                                        };
                                        const res = await adminAPI.generatePaymentReceipt(selectedAffiliate.id, payload);
                                        const w = window.open();
                                        w.document.write(res.data.html);
                                        w.document.close();
                                        setTimeout(() => w.print(), 300);
                                        await adminAPI.markCommissionPaid(selectedAffiliate.id, commission.id);
                                        toast.success('Commission marked as paid');
                                        loadReferralData(selectedAffiliate);
                                        fetchAffiliates();
                                      } catch (err) {
                                        console.error('Failed to mark paid', err);
                                        toast.error('Failed to complete action');
                                      }
                                    }}
                                  >
                                    Mark Paid
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter className="flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => handleExportAffiliateFullReport(selectedAffiliate)}
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                Export This Affiliate CSV
              </Button>
              <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

